import React, { useEffect, useMemo, useState, useCallback } from 'react'
import {
  CheckInActiveApi,
  CompanyHostApi,
  companyServices,
  driverCheckInServices
} from '../../domain'
import { useIPNetwork } from '../../hooks/useIPNetwork'
import { useWindowLocationHost } from '../../hooks/useWindowLocationHost'
import webSocketClient from '../../webSocketClient'
import { DriverTVContent } from './components/DriverTVContent'
import { DriverTVLoading } from './components/DriverTVLoading'
import styles from './styles.module.css'
import { fetchDriversInAttendance } from './utils'


export type DriverTV = CheckInActiveApi
export type DriverCalled = CheckInActiveApi

export type TV = {
  driverTv: DriverTV
  inAttendance: boolean
}

export function DriversTV() {
  const [isWsListenerAdded, setIsWsListenerAdded] = useState(false)
  const [showTV, setShowTV] = useState(false)

  const [drivers, setDrivers] = useState<TV[]>([])
  const [lastDriverCalled, setLastDriverCalled] = useState<DriverCalled | null>(
    null
  )
  const [loading, setLoading] = useState(true)
  const [company, setCompany] = useState<CompanyHostApi | null>(null)

  console.log("🏁 DriversTV - Estado atual lastDriverCalled:", lastDriverCalled?.queue?.driver?.name || "null")

  const driverIp = useIPNetwork()
  const { windowLocationHost } = useWindowLocationHost()

  // const shippingCompanyId = localStorage.getItem('@App:companyId')!;
  // const companyIdIsSame = +shippingCompanyId === company?.id;

  const handleTVData = useCallback(async (companyCheckinIps: string, companyId: string) => {
    const driverIpValid = companyCheckinIps
      .replace(' ', '')
      .split(',')
      .includes(driverIp)

    if (!driverIpValid) return

    const [inAttendance, driversCheckin] = await Promise.all([
      fetchDriversInAttendance(companyId),
      driverCheckInServices.getDriversCalled(companyId)
    ])

    let formattedDriverList = driversCheckin
      .filter(driver => driver.queue)

    // Não define lastDriverCalled aqui para evitar chamadas automáticas
    // Apenas o websocket deve disparar as chamadas de voz

    setDrivers(
      formattedDriverList.map(item => {
        const truckId = item.queue?.truck?.id
        const isAttendanceDriver = inAttendance.includes(truckId)

        return {
          driverTv: item,
          inAttendance: isAttendanceDriver
        }
      })
    )
  }, [driverIp])

  const fetchDataPage = useCallback(async () => {
    if (!driverIp) return

    setLoading(true)
    try {
      const company = await companyServices.getCompanyByHost(windowLocationHost)
      setCompany(company)

      const companyId = company.id.toString()

      localStorage.setItem('@App:companyId', companyId)

      const [companySettings, companyAttendanceStatus] = await Promise.all([
        companyServices.getSettings(companyId),
        companyServices.getAttendanceStatus(companyId)
      ])

      if (companyAttendanceStatus) {
        setShowTV(companyAttendanceStatus.status)
      }

      await handleTVData(companySettings.checkinIp, companyId)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [driverIp, windowLocationHost, handleTVData])

  const attended = drivers.filter(driver => driver.inAttendance)
  const attendedTotal = attended.length

  const driversEmpty = useMemo(() => drivers.length === 0, [drivers])

  //TODO Requisição websocket para verificar se o motorista foi chamado
  useEffect(() => {
    const handleWebSocketResponse = async (message: {
      driverCalled: DriverCalled | null
    }) => {
      console.log("🎯 DriversTV - WebSocket recebido:", message.driverCalled?.queue?.driver?.name)

      if (message.driverCalled && !isWsListenerAdded) {
        setIsWsListenerAdded(true)
      }

      // Atualiza imediatamente o lastDriverCalled para que a voz funcione
      if (message.driverCalled) {
        console.log("📢 DriversTV - Definindo lastDriverCalled via WebSocket:", message.driverCalled.queue?.driver?.name)
        setLastDriverCalled(message.driverCalled)
      }

      // Depois busca os dados atualizados
      await fetchDataPage()
    }

    webSocketClient.on('checkInCallResponse', handleWebSocketResponse)

    return () => {
      webSocketClient.off('checkInCallResponse', handleWebSocketResponse)
      setIsWsListenerAdded(false)
    }
  }, [fetchDataPage, isWsListenerAdded])

  useEffect(() => {
    fetchDataPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowLocationHost, driverIp, isWsListenerAdded])

  return (
    <div className={styles['tv-container']}>
      {loading || driversEmpty || !showTV ? (
        <DriverTVLoading />
      ) : (
        <DriverTVContent
          lastDriverCalled={lastDriverCalled}
          attendeds={attendedTotal}
          tvList={drivers}
          company={company}
          refetchDriverList={fetchDataPage}
        />
      )}
    </div>
  )
}
